/**
 * Hook for fetching full email thread data
 *
 * Returns the complete email conversation for display in slide-out panel
 */

import { useQuery } from '@tanstack/react-query';

import { fetchWithDefaults } from '@/lib/http/client';

import type { ThreadData } from '../core/types';
import { proposalQueryKeys } from '../query/keys';

/**
 * Fetch thread data for a proposal action
 */
async function fetchThreadData(
  orgId: number,
  proposalId: number,
  actionId: number
): Promise<ThreadData | null> {
  const url = `/api/orgs/${orgId}/proposals/${proposalId}/actions/${actionId}/?include=thread`;

  const response = await fetchWithDefaults(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    // Throw error for panel to show retry button
    throw new Error(`Failed to fetch thread: ${response.status}`);
  }

  const data = await response.json();
  return data.thread ?? null;
}

interface UseEmailThreadOptions {
  /** Whether to enable the query (default: true) */
  enabled?: boolean;
}

interface UseEmailThreadResult {
  /** Thread data */
  data: ThreadData | null | undefined;
  /** Whether the query is loading */
  isLoading: boolean;
  /** Whether the query encountered an error */
  isError: boolean;
  /** Error object if any */
  error: Error | null;
  /** Refetch function for retry button */
  refetch: () => void;
}

/**
 * Hook for fetching full email thread data
 *
 * Use this hook when opening the EmailThreadPanel - it lazy-loads thread data
 * only when needed.
 *
 * @param orgId - Organization ID
 * @param proposalId - Proposal ID
 * @param actionId - Action ID (the gmail_send action)
 * @param options - Query options
 * @returns Query result with thread data
 *
 * @example
 * ```typescript
 * const { data, isLoading, isError, refetch } = useEmailThread(
 *   orgId,
 *   proposalId,
 *   actionId,
 *   { enabled: isPanelOpen }
 * );
 *
 * if (isLoading) return <LoadingSpinner />;
 * if (isError) return <RetryButton onClick={refetch} />;
 * if (!data) return <EmptyState />;
 *
 * return (
 *   <div>
 *     {data.messages.map(msg => (
 *       <EmailMessage key={msg.id} message={msg} />
 *     ))}
 *   </div>
 * );
 * ```
 */
export function useEmailThread(
  orgId: number,
  proposalId: number,
  actionId: number,
  options: UseEmailThreadOptions = {}
): UseEmailThreadResult {
  const { enabled = true } = options;

  const query = useQuery({
    queryKey: proposalQueryKeys.thread(orgId, proposalId, actionId),
    queryFn: () => fetchThreadData(orgId, proposalId, actionId),
    enabled: enabled && actionId > 0,
    // Cache thread data for session (10 minutes)
    staleTime: 10 * 60_000,
    gcTime: 30 * 60_000,
    // Retry on failure - user may want to retry
    retry: 2,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 10000),
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
